/** Compact read-only preview of an included ruleset's rules — the right-hand
 *  panel of the "Include ruleset" editor. One line per rule (NOT · condition ·
 *  summary); nested includes are NOT expanded (one level by design) — they
 *  link to the target ruleset instead. */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Link, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import * as rulesetsApi from '@/api/notificationRulesets';
import { MATCH_ANY } from '@/api/notificationRulesets';

import { buildRulesetPreviewRows } from './ruleSummary';

export function RulesetPreview({ rulesetId }: { rulesetId: number }) {
  const query = useQuery({
    queryKey: ['notification_ruleset', rulesetId],
    queryFn: () => rulesetsApi.retrieve(rulesetId),
    staleTime: 60_000,
  });

  if (query.isError) {
    return (
      <Typography variant="caption" color="text.secondary">
        Rules preview unavailable.
      </Typography>
    );
  }

  const detail = query.data;
  const rows = detail ? buildRulesetPreviewRows(detail.rules, detail.refs) : [];

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.default',
        px: 1.5,
        py: 1,
        maxHeight: 190,
        overflowY: 'auto',
      }}
    >
      {!detail ? (
        <Stack spacing={0.5}>
          <Skeleton width="55%" />
          <Skeleton width="80%" />
          <Skeleton width="70%" />
        </Stack>
      ) : rows.length === 0 ? (
        <Typography variant="caption" color="text.secondary">
          No rules — this ruleset always passes.
        </Typography>
      ) : (
        <>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', fontWeight: 600, mb: 0.25 }}
          >
            Matches {detail.match_type === MATCH_ANY ? 'ANY' : 'ALL'} of {rows.length} rule
            {rows.length === 1 ? '' : 's'}:
          </Typography>
          {rows.map((row) => (
            <Typography
              key={row.key}
              variant="caption"
              noWrap
              sx={{ display: 'block', lineHeight: 1.7 }}
              title={`${row.negate ? 'NOT ' : ''}${row.label}${row.summary ? ` — ${row.summary}` : ''}`}
            >
              {row.negate && (
                <Box component="span" sx={{ color: 'error.main', fontWeight: 700, mr: 0.5 }}>
                  NOT
                </Box>
              )}
              <Box component="span" sx={{ fontWeight: 600 }}>
                {row.label}
              </Box>
              {row.summary &&
                (row.nestedRulesetId ? (
                  <>
                    {' '}
                    <Link
                      component={RouterLink}
                      to={`/notifications/rulesets/${row.nestedRulesetId}`}
                      target="_blank"
                      rel="noopener"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.summary}
                      <OpenInNewIcon sx={{ fontSize: 11, ml: 0.25, verticalAlign: 'middle' }} />
                    </Link>
                  </>
                ) : (
                  <Box component="span" sx={{ color: 'text.secondary' }}>
                    {' — '}
                    {row.summary}
                  </Box>
                ))}
            </Typography>
          ))}
        </>
      )}
    </Box>
  );
}
